// Brand page shop model. Pure and client-safe: everything a brand page shows
// (the lineup rail, type and line chips, sort, filters, URL state) is derived
// from the brand's own products, so every brand gets the same treatment with
// no per-brand code. The only per-brand input is the optional tagline below.

import type { CatalogLite } from "./types";
import { byNewest, classify, populatedTypes } from "./gender";
import { brandSlug, joinWords } from "./brands";

/** Above this many products a brand page falls back to the plain paginated grid. */
export const BRAND_SHOP_MAX = 96;

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

/**
 * Optional hand-written brand line in the boutique's voice. A brand without an
 * entry gets a line built from its lines and types (brandTagline). Keep these
 * factual: what the brand makes and that we carry it, nothing invented. Taglines
 * must not claim stock or availability; that lives in the data-driven intro
 * (seoCopy), which counts only in-stock pieces.
 */
export const BRAND_COPY: Record<string, { tagline: string }> = {
  godspeed: {
    tagline:
      "Forever truckers, OG Logo beanies and the Insignia hat. The Godspeed headwear lineup, colorway by colorway.",
  },
};

export function brandTagline(slug: string, label: string, lines: LineChip[], typeLine: string): string {
  const custom = BRAND_COPY[slug]?.tagline;
  if (custom) return custom;
  const names = lines.slice(0, 4).map((l) => l.label);
  const lead = names.length >= 2 ? `${joinWords(names)}. ` : "";
  return `${lead}${label} ${typeLine} at Muñeca's Couture.`;
}

// ---------------------------------------------------------------------------
// Sort + filter state (mirrored in the URL as ?type=&line=&stock=in&sort=)
// ---------------------------------------------------------------------------

export type SortKey = "featured" | "newest" | "price-asc" | "price-desc" | "name";

export const SORTS: { key: SortKey; label: string }[] = [
  { key: "featured", label: "Featured" },
  { key: "newest", label: "Newest" },
  { key: "price-asc", label: "Price, low to high" },
  { key: "price-desc", label: "Price, high to low" },
  { key: "name", label: "Name, A to Z" },
];

export interface BrandShopState {
  /** Garment type slug (lib/gender.ts), e.g. "caps". */
  type?: string;
  /** Line slug from brandLines, e.g. "forever". */
  line?: string;
  /** Only in-stock pieces. */
  stock: boolean;
  sort: SortKey;
}

export const DEFAULT_STATE: BrandShopState = { stock: false, sort: "featured" };

export function isFiltered(s: BrandShopState): boolean {
  return Boolean(s.type || s.line || s.stock || s.sort !== "featured");
}

export interface BrandQuery {
  type?: string;
  line?: string;
  stock?: string;
  sort?: string;
}

/** Query string -> state, dropping values the brand does not have. */
export function parseState(q: BrandQuery, types: TypeChip[], lines: LineChip[]): BrandShopState {
  const type = q.type && types.some((t) => t.slug === q.type) ? q.type : undefined;
  const line = q.line && lines.some((l) => l.slug === q.line) ? q.line : undefined;
  const sort = SORTS.some((s) => s.key === q.sort) ? (q.sort as SortKey) : "featured";
  return { type, line, stock: q.stock === "in", sort };
}

/** State -> "?type=caps&line=forever" ("" when default). */
export function stateToQuery(s: BrandShopState): string {
  const p = new URLSearchParams();
  if (s.type) p.set("type", s.type);
  if (s.line) p.set("line", s.line);
  if (s.stock) p.set("stock", "in");
  if (s.sort !== "featured") p.set("sort", s.sort);
  const qs = p.toString();
  return qs ? `?${qs}` : "";
}

// ---------------------------------------------------------------------------
// Lines: "GS Forever Trucker Hat (Vanta)" -> line "Forever", colorway "Vanta".
// A line is what is left of a product name once the brand, its abbreviation,
// SKU-like tokens, type nouns and the trailing colorway are removed, so the
// colorways of one design group together without any brand-specific list.
// ---------------------------------------------------------------------------

const TYPE_NOUN_RE =
  /\b(hats?|caps?|truckers?|snapbacks?|beanies?|visors?|tees?|t-?shirts?|tshirts?|polos?|shirts?|hoodies?|sweatshirts?|crewnecks?|jackets?|coats?|pants|joggers?|jeans|shorts|sets?|dress(?:es)?|skirts?|jumpsuits?|bodysuits?|fragrances?|cologne|perfume)\b/gi;
/** Vendor codes like FRF3031 or FRP3125BS: 5+ chars of capitals/digits with at least one digit. */
const SKU_TOKEN_RE = /\b(?=[A-Z0-9-]{5,}\b)(?=[A-Z0-9-]*\d)[A-Z0-9-]+\b/g;

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export interface LineRef {
  /** The design name shared by its colorways; "" when nothing is left. */
  stem: string;
  /** Trailing parenthetical, e.g. "Black/Red". */
  colorway?: string;
}

export function productLine(p: Pick<CatalogLite, "name" | "brand">): LineRef {
  let name = p.name.trim();
  let colorway: string | undefined;
  const paren = name.match(/\(([^()]*)\)\s*$/);
  if (paren) {
    colorway = paren[1].trim() || undefined;
    name = name.slice(0, paren.index).trim();
  }
  const brand = (p.brand ?? "").trim();
  if (brand) {
    name = name.replace(new RegExp(`\\b${escapeRe(brand)}\\b`, "gi"), " ");
    // A leading 2–3 letter abbreviation that starts with the brand's initial
    // ("GS" for Godspeed, "FR" for First Row) is the brand again.
    const initial = brand[0].toUpperCase();
    name = name.replace(new RegExp(`^\\s*${escapeRe(initial)}[A-Z]{1,2}(?=\\s|$)`), " ");
  }
  name = name.replace(/^\s*(?:x|×)\s+/i, " ");
  name = name.replace(SKU_TOKEN_RE, " ");
  name = name.replace(TYPE_NOUN_RE, " ");
  name = name
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^[-–—:|]+|[-–—:|]+$/g, "")
    .trim();
  // A trailing short all-caps token ("... OG") is a variant tag, not a line.
  name = name.replace(/\s+[A-Z]{1,3}$/, "").trim();
  return { stem: name, colorway };
}

export interface LineChip {
  slug: string;
  label: string;
  count: number;
  /** The product whose photo represents the line (the base colorway when there is one). */
  lead: CatalogLite;
}

/**
 * Lines with two or more colorways, biggest first. Returns [] unless at least
 * two such lines exist, so brands of one-off pieces show no line chips.
 */
export function brandLines(products: CatalogLite[]): LineChip[] {
  const map = new Map<string, LineChip & { leadIsBase: boolean }>();
  for (const p of [...products].sort(byNewest)) {
    const { stem, colorway } = productLine(p);
    if (!stem) continue;
    const slug = brandSlug(stem);
    if (!slug) continue;
    const isBase = !colorway && Boolean(p.image);
    const cur = map.get(slug);
    if (!cur) {
      map.set(slug, { slug, label: stem, count: 1, lead: p, leadIsBase: isBase });
    } else {
      cur.count += 1;
      if ((!cur.leadIsBase && isBase) || !cur.lead.image) {
        cur.lead = p;
        cur.leadIsBase = isBase;
      }
    }
  }
  const lines = [...map.values()]
    .filter((l) => l.count >= 2)
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
    .map(({ slug, label, count, lead }) => ({ slug, label, count, lead }));
  return lines.length >= 2 ? lines : [];
}

export function lineSlugOf(p: Pick<CatalogLite, "name" | "brand">): string {
  return brandSlug(productLine(p).stem);
}

// ---------------------------------------------------------------------------
// Types (Caps & Hats / Beanies / Tees ...), from the shared garment taxonomy
// ---------------------------------------------------------------------------

export interface TypeChip {
  slug: string;
  label: string;
  count: number;
}

/** Populated garment types for the brand; [] unless there are at least two. */
export function brandTypes(products: CatalogLite[]): TypeChip[] {
  const { groups, singles } = populatedTypes(products);
  const types: TypeChip[] = [...groups.flatMap((g) => g.types), ...singles];
  return types.length >= 2 ? types : [];
}

// ---------------------------------------------------------------------------
// Apply state
// ---------------------------------------------------------------------------

export function sortProducts(list: CatalogLite[], sort: SortKey): CatalogLite[] {
  const arr = [...list];
  switch (sort) {
    case "newest":
      return arr.sort(byNewest);
    case "price-asc":
      return arr.sort((a, b) => a.priceNumber - b.priceNumber || a.name.localeCompare(b.name));
    case "price-desc":
      return arr.sort((a, b) => b.priceNumber - a.priceNumber || a.name.localeCompare(b.name));
    case "name":
      return arr.sort((a, b) => a.name.localeCompare(b.name));
    default:
      // Featured: whatever can be bought right now first, then newest. When
      // stock lands, those pieces float to the top with no code change.
      return arr.sort((a, b) => Number(b.inStock) - Number(a.inStock) || byNewest(a, b));
  }
}

export function applyState(products: CatalogLite[], s: BrandShopState): CatalogLite[] {
  let list = products;
  if (s.type) list = list.filter((p) => classify(p).type === s.type);
  if (s.line) list = list.filter((p) => lineSlugOf(p) === s.line);
  if (s.stock) list = list.filter((p) => p.inStock);
  return sortProducts(list, s.sort);
}

// ---------------------------------------------------------------------------
// Hero rail tiles
// ---------------------------------------------------------------------------

export interface RailTile {
  href: string;
  label: string;
  sub: string;
  image?: string;
  alt: string;
  /** Set when the tile filters the grid to a line instead of opening a product. */
  line?: string;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/**
 * One tile per line when the brand has lines; otherwise the newest products
 * (so a brand of one-off pieces still opens with real product photos).
 */
export function heroTiles(brandSlugValue: string, products: CatalogLite[], lines: LineChip[], max = 8): RailTile[] {
  if (lines.length > 0) {
    return lines.slice(0, max).map((l) => ({
      href: `/brands/${brandSlugValue}?line=${l.slug}#shop`,
      label: l.label,
      sub: plural(l.count, "style", "styles"),
      image: l.lead.image,
      alt: `${l.label}: ${l.lead.name}`,
      line: l.slug,
    }));
  }
  return [...products]
    .filter((p) => p.image)
    .sort(byNewest)
    .slice(0, max)
    .map((p) => ({ href: `/shop/${p.slug}`, label: tileName(p.name).base, sub: p.price, image: p.image, alt: p.name }));
}

// ---------------------------------------------------------------------------
// Tile presentation helpers
// ---------------------------------------------------------------------------

/** "GS Forever Trucker Hat (Vanta)" -> base "GS Forever Trucker Hat", colorway "Vanta". */
export function tileName(name: string): { base: string; colorway?: string } {
  const m = name.trim().match(/^(.*?)\s*\(([^()]+)\)\s*$/);
  return m ? { base: m[1].trim(), colorway: m[2].trim() } : { base: name.trim() };
}

/** Colour words that appear in colorway names, mapped to a swatch. Unknown words mean no swatches. */
const COLOR_WORDS: Record<string, string | string[]> = {
  black: "#141414",
  vanta: "#141414",
  white: "#FFFFFF",
  bone: "#E9E2D0",
  cream: "#EFE6D2",
  creme: "#EFE6D2",
  grey: "#8E8E8E",
  gray: "#8E8E8E",
  silver: "#B8B8B8",
  navy: "#1F2A44",
  red: "#C8102E",
  cardinal: "#B3202C",
  wine: "#6E2A3A",
  "red wine": "#6E2A3A",
  burgundy: "#6D1F2E",
  maroon: "#6D1F2E",
  blue: "#1E4FD8",
  royal: "#2C4FB5",
  teal: "#1F8A8A",
  orange: "#F26B1D",
  yellow: "#F2C230",
  gold: "#C9A452",
  green: "#2E7D32",
  shamrock: "#2E8B57",
  olive: "#6B6B3A",
  lime: "#B6E23A",
  purple: "#6A3BA8",
  lavender: "#B7A3E3",
  pink: "#E86AA3",
  fuchsia: "#D4007A",
  "fuchsia pink": "#D4007A",
  tan: "#C8A977",
  khaki: "#B3A27A",
  brown: "#6B4A2B",
  oreo: ["#141414", "#FFFFFF"],
};

/** Swatch colours for a colorway like "Black/Red"; undefined when any word is unknown. */
export function colorDots(colorway: string): string[] | undefined {
  const out: string[] = [];
  for (const part of colorway.toLowerCase().split(/\s*\/\s*/)) {
    const whole = COLOR_WORDS[part.trim()];
    const hits = whole ? [whole] : part.split(/\s+/).map((w) => COLOR_WORDS[w]);
    if (hits.some((h) => !h)) return undefined;
    for (const h of hits) for (const c of Array.isArray(h) ? h : [h as string]) if (!out.includes(c)) out.push(c);
  }
  return out.length ? out.slice(0, 4) : undefined;
}
